import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Eye, Brain, Zap, ShieldCheck, Layers, GitBranch, Bug, Wrench,
  TrendingDown, Cpu, Network, FileText, Sparkles, Lock, ChevronRight, Activity, Code2,
} from "lucide-react";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import DesignPartnerDialog from "../components/argus/DesignPartnerDialog";
import { Button } from "../components/ui/button";

// ---------------- helpers ----------------

const Pillar = ({ index, label, title, blurb, Icon }) => (
  <article className="rounded-2xl border border-line bg-panel p-7" data-testid={`pillar-${label.toLowerCase()}`}>
    <div className="flex items-center gap-3 mb-4">
      <span className="font-mono text-xs tracking-[0.2em] text-gold/80">0{index}</span>
      <span className="text-xs uppercase tracking-[0.25em] text-ink/50">{label}</span>
    </div>
    <h3 className="font-display text-3xl mb-3 leading-tight">{title}</h3>
    <p className="text-ink/70 leading-relaxed text-[15px]">{blurb}</p>
    <div className="mt-6 inline-flex items-center justify-center w-10 h-10 rounded-full bg-gold/10 text-gold">
      <Icon size={18} />
    </div>
  </article>
);

const Capability = ({ Icon, title, blurb, to, ctaLabel = "Learn more", comingSoon = false }) => {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
  <article
    className={`rounded-xl border bg-panel p-6 flex flex-col ${comingSoon ? "border-gold/30 opacity-80" : "border-line"}`}
    data-testid={`capability-${slug}`}
    aria-disabled={comingSoon ? "true" : undefined}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-gold" />
      <span className="text-[10px] uppercase tracking-widest text-ink/40">Capability</span>
      {comingSoon && (
        <span
          className="ml-auto text-[9px] uppercase tracking-wider text-gold border border-gold/40 bg-gold/10 rounded-full px-2 py-0.5 whitespace-nowrap"
          data-testid={`capability-coming-soon-${slug}`}
        >
          Coming soon
        </span>
      )}
    </div>
    <h3 className="font-display text-2xl mb-2 leading-snug">{title}</h3>
    <p className="text-ink/70 text-sm leading-relaxed flex-1">{blurb}</p>
    {to && !comingSoon && (
      <Link to={to} className="mt-4 text-sm text-gold hover:underline inline-flex items-center gap-1" data-testid={`capability-link-${slug}`}>
        {ctaLabel} <ChevronRight size={14} />
      </Link>
    )}
    {comingSoon && (
      <span className="mt-4 text-xs text-ink/50 italic">On the roadmap — not yet available.</span>
    )}
  </article>
  );
};

const FaqItem = ({ q, a }) => (
  <details className="rounded-lg border border-line bg-panel p-5 group" data-testid="faq-item">
    <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
      <span className="font-display text-xl text-ink">{q}</span>
      <ChevronRight size={16} className="text-ink/40 group-open:rotate-90 transition-transform" />
    </summary>
    <div className="mt-3 text-ink/80 text-[15px] leading-relaxed space-y-3">{a}</div>
  </details>
);

// ---------------- architecture diagram ----------------

function ArchitectureDiagram() {
  return (
    <div className="rounded-2xl border border-line bg-panel p-6 lg:p-10" data-testid="argus-platform-architecture">
      <h3 className="font-display text-2xl mb-1">ArgusSecure Platform</h3>
      <p className="text-ink/60 text-sm uppercase tracking-wider mb-6">Risk Reduction Outcomes</p>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="arch-outcomes">
        {[
          "Complete Code & App Visibility",
          "Attack Path & Reachability",
          "Predictive Risk Prioritization",
          "Automated Remediation",
          "Business-Aligned Risk Metrics",
        ].map((o) => (
          <span key={o} className="text-xs px-3 py-1.5 rounded-full bg-gold/10 text-gold border border-gold/30">
            {o}
          </span>
        ))}
      </div>

      <div className="space-y-3">
        {/* Hexa-like agent layer → Fix-It */}
        <div className="rounded-lg border border-gold/40 bg-gold/5 p-5" data-testid="arch-layer-agentic">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-gold" />
            <h4 className="font-semibold text-ink">Fix-It Agentic Layer</h4>
          </div>
          <p className="text-ink/70 text-sm mb-3">ArgusSecure built-in, customer-supplied, and CI/CD agents. Humans in control.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {["Workflows", "Remediation", "Orchestration", "Verify Exploitability"].map((x) => (
              <div key={x} className="text-xs rounded-md bg-paper border border-line px-3 py-2 text-center font-mono text-ink/70">{x}</div>
            ))}
          </div>
        </div>

        {/* Exposure Data Fabric */}
        <div className="rounded-lg border border-line bg-paper p-5" data-testid="arch-layer-fabric">
          <div className="flex items-center gap-2 mb-2">
            <Layers size={16} className="text-gold" />
            <h4 className="font-semibold text-ink">Exposure Data Fabric</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {["Analyze", "Correlate", "Enrich", "Verify Exploitability", "Score Risk", "Map Relationships"].map((x) => (
              <div key={x} className="text-xs rounded-md bg-panel border border-line px-3 py-2 text-center font-mono text-ink/70">{x}</div>
            ))}
          </div>
        </div>

        {/* Surfaces and Signals */}
        <div className="rounded-lg border border-line bg-paper p-5" data-testid="arch-layer-signals">
          <div className="flex items-center gap-2 mb-3">
            <Network size={16} className="text-gold" />
            <h4 className="font-semibold text-ink">Surfaces & Signals</h4>
            <span className="text-xs text-ink/40 ml-auto">Open data integrations</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-ink/40 mb-1.5">ArgusSecure sensors</p>
              <p className="text-ink/70 text-xs">SAST · DAST · SCA · ASM (preview)</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-ink/40 mb-1.5">Attack surface</p>
              <p className="text-ink/70 text-xs">Code · Apps · Identity · Cloud · APIs · Web</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-ink/40 mb-1.5">Threat intelligence</p>
              <p className="text-ink/70 text-xs">CWE · CVE · OWASP Top 10 · MITRE ATT&CK</p>
            </div>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-ink/40 mt-4 italic">
        Connector ecosystem in active development. Today: GitHub repo connector + npm/PyPI manifest ingestion are GA; Slack / Jira / GitHub-App PR-checks land in the next release.
      </p>
    </div>
  );
}

// ---------------- page ----------------

export default function PlatformOverview() {
  return (
    <div className="bg-paper text-ink min-h-screen">
      <MarketingHeader />

      {/* HERO */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4" data-testid="hero-eyebrow">
              ArgusSecure · Platform
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight" data-testid="platform-hero-title">
              Take action on application risk.
            </h1>
            <p className="mt-6 text-xl text-ink/75 max-w-2xl leading-relaxed" data-testid="platform-hero-sub">
              The ArgusSecure Platform unifies code-to-cloud application risk —
              SAST, DAST, SCA, and AI-driven Fix-It — into a single exposure-management
              workflow. Find it. Prove it. Fix it.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button className="bg-gold text-paper hover:bg-gold2 h-12 px-6 font-semibold" data-testid="platform-hero-cta">
                  Start My Free Scan <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <DesignPartnerDialog
                source="platform-hero"
                trigger={
                  <Button variant="outline" className="h-12 px-6 border-line bg-transparent text-ink hover:bg-raised">
                    Talk to Security
                  </Button>
                }
              />
            </div>
            <p className="text-xs text-ink/40 mt-4 font-mono">
              No credit card · Demo org pre-loaded · 60-second onboarding
            </p>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-2xl border border-line bg-panel p-6">
              <h3 className="font-display text-xl mb-4">The integrated AppSec stack</h3>
              <ul className="space-y-3 text-sm">
                {[
                  ["SAST", "Static analysis on every push — 15+ rules, JS/TS/Python today."],
                  ["DAST", "Real HTTP probing with SSRF guards + domain verification."],
                  ["SCA", "npm + PyPI manifests against curated CVE feed."],
                  ["AI Fix-It", "Claude Sonnet 4.5 produces patches, not platitudes."],
                  ["Exposure Analytics", "Risk score per project · attack path overlays · audit-ready PDFs."],
                ].map(([k, v]) => (
                  <li key={k} className="flex gap-3">
                    <span className="font-mono text-[11px] text-gold w-16 shrink-0 pt-0.5">{k}</span>
                    <span className="text-ink/80">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="border-b border-line" id="pillars">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="font-display text-4xl mb-3" data-testid="pillars-title">Take command of your entire application surface.</h2>
          <p className="text-ink/65 max-w-2xl mb-12">Three steps. The same three steps every honest AppSec programme runs.</p>
          <div className="grid md:grid-cols-3 gap-5" data-testid="pillars-grid">
            <Pillar
              index={1}
              label="Visibility"
              title="Uncover your complete exposure landscape."
              blurb="Connect a repo, paste a manifest, or point ArgusSecure at a verified domain. We map every line of code, every dependency, every route — and we don't make you guess what we found."
              Icon={Eye}
            />
            <Pillar
              index={2}
              label="Insight"
              title="Understand the exposure that puts your business at risk."
              blurb="Correlate findings across SAST, DAST, and SCA. Reachability, severity, CVSS, OWASP category — we translate static-analyzer noise into one risk score that engineers and execs both trust."
              Icon={Brain}
            />
            <Pillar
              index={3}
              label="Action"
              title="Take action on the risks that matter most."
              blurb="The embedded Fix-It Agent reads each finding's evidence and produces a concrete patch — code diff, dependency bump, or config change. Copy, paste, merge, ship."
              Icon={Zap}
            />
          </div>
        </div>
      </section>

      {/* PLATFORM ARCHITECTURE */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <ArchitectureDiagram />
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="font-display text-4xl mb-3">All types of code. All exposures. All in.</h2>
          <p className="text-ink/65 max-w-2xl mb-12">
            One platform spanning the modern AppSec surface — so you expose and close cyber weaknesses anywhere
            to reduce business risk everywhere.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="capabilities-grid">
            <Capability
              Icon={Eye}
              title="Know your attack surface"
              blurb="A trusted view of every code path, dependency, and HTTP route. No asset or exposure escapes scrutiny."
              to="/products/sast"
            />
            <Capability
              Icon={TrendingDown}
              title="Focus on what matters most"
              blurb="Direct remediation at the toxic combinations — reachable + critical + exploitable — across SAST, DAST, and SCA."
              to="/products/sast"
            />
            <Capability
              Icon={FileText}
              title="Simplify risk insights"
              blurb="Business-aligned exposure analytics, customizable dashboards, and audit-ready PDFs you can hand to a board."
              to="/products/compliance"
              comingSoon
            />
            <Capability
              Icon={Sparkles}
              title="Control AI-driven risk"
              blurb="Surface prompt-injection-prone endpoints, leaked LLM keys, and weak guardrails before adversaries do."
              to="/solutions/ai-security"
              comingSoon
            />
            <Capability
              Icon={Cpu}
              title="Cloud & Container scanning"
              blurb="Today: live AWS S3 public-bucket scanner. Next: container image SCA + IaC misconfiguration detection."
              to="/products/sca"
              comingSoon
            />
            <Capability
              Icon={Network}
              title="Unify tools & data"
              blurb="GitHub repo connector live. npm + PyPI manifests parsed natively. Slack, Jira, GitHub-App PR checks land next."
              to="/resources"
            />
            <Capability
              Icon={Activity}
              title="Streamline risk management"
              blurb="Enforce consistent hygiene standards and silence low-value noise so teams focus on the high-risk handful."
              to="/products/remediation"
            />
            <Capability
              Icon={Wrench}
              title="Automate risk reduction"
              blurb="Deploy the Fix-It Agent on every finding. Inherits your evidence and remediation logic. Humans approve every merge."
              to="/products/remediation"
            />
            <Capability
              Icon={Code2}
              title="Accelerate threat analysis"
              blurb="Unified evidence: request/response pairs, code snippets, dependency graphs, fix proposals — one drawer, one decision."
              to="/products/dast"
            />
          </div>
        </div>
      </section>

      {/* OUTCOMES — honest version */}
      <section className="border-b border-line bg-panel/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="font-display text-4xl mb-3">What our design partners are pursuing.</h2>
          <p className="text-ink/65 max-w-2xl mb-10">
            We're onboarding our first cohort of design partners now. These are the targets they've set — not fabricated benchmarks. We'll publish real outcomes here as our partners hit them.
          </p>
          <div className="grid sm:grid-cols-3 gap-5" data-testid="outcomes-grid">
            {[
              { eyebrow: "Target", value: "70%", label: "fewer tickets via contextual prioritization of true exposure" },
              { eyebrow: "Target", value: "3x",  label: "faster MTTR with embedded AI Fix-It patches" },
              { eyebrow: "Target", value: "100%", label: "code-to-cloud visibility — SAST, DAST, SCA in one tab" },
            ].map((o) => (
              <div key={o.label} className="rounded-xl border border-line bg-panel p-7">
                <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">{o.eyebrow}</p>
                <div className="font-display text-6xl text-ink mb-3">{o.value}</div>
                <p className="text-ink/70 text-sm leading-relaxed">{o.label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-ink/40 mt-6 italic">
            ArgusSecure publishes only verified outcomes from named design partners. No analyst quotes, no fabricated logos — ever.
          </p>
        </div>
      </section>

      {/* DESIGN PARTNER CTA in place of customer-stories carousel */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-display text-4xl mb-4">Want your team quoted here?</h2>
            <p className="text-ink/70 text-lg leading-relaxed mb-6">
              ArgusSecure is onboarding its first cohort of design partners — security teams shipping
              code daily who want to fix earlier and prove it. You bring the targets, we bring the engineers.
              No sales gauntlet. A real human reply within one business day.
            </p>
            <DesignPartnerDialog
              source="platform-stories"
              trigger={
                <Button className="bg-gold text-paper hover:bg-gold2 h-12 px-6 font-semibold" data-testid="platform-partner-cta">
                  Apply to be a design partner <ArrowRight size={16} className="ml-2" />
                </Button>
              }
            />
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-xl border border-line bg-panel p-6">
              <h3 className="font-display text-2xl mb-4 flex items-center gap-2"><Lock size={18} className="text-gold" /> What you get</h3>
              <ul className="space-y-2.5 text-sm text-ink/80">
                {[
                  "Pro-tier features on us during the engagement",
                  "Direct line to the engineers building the platform",
                  "Roadmap input on the next two releases",
                  "Co-marketing on ArgusSecure pages (your call, with your approval)",
                  "Zero exclusivity — drop out any time",
                ].map((line) => (
                  <li key={line} className="flex gap-2"><span className="text-gold">·</span>{line}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="font-display text-4xl mb-10" data-testid="platform-faq-title">FAQs</h2>
          <div className="space-y-3" data-testid="platform-faq">
            <FaqItem
              q="What is the ArgusSecure Platform?"
              a={(
                <>
                  <p>An exposure-management platform built for application risk. ArgusSecure unifies SAST (static), DAST (runtime), and SCA (dependencies) into a single workflow with evidence-backed findings and an embedded Fix-It Agent.</p>
                  <p>The platform answers four questions every AppSec team has: <strong>What's exposed?</strong> · <strong>How exploitable is it?</strong> · <strong>What do we fix first?</strong> · <strong>What's the patch?</strong></p>
                </>
              )}
            />
            <FaqItem
              q="What products are included?"
              a={(
                <ul className="list-disc pl-5 space-y-1">
                  <li><Link to="/products/sast" className="text-gold underline">SAST — Static analysis</Link></li>
                  <li><Link to="/products/dast" className="text-gold underline">DAST — Runtime probing</Link></li>
                  <li><Link to="/products/sca" className="text-gold underline">SCA — Dependency scanning</Link></li>
                  <li><Link to="/products/asm" className="text-gold underline">Attack Surface Management (preview)</Link></li>
                  <li><Link to="/products/remediation" className="text-gold underline">Remediation — Fix-It Agent</Link></li>
                  <li><Link to="/products/compliance" className="text-gold underline">Compliance — Audit-ready reporting</Link></li>
                </ul>
              )}
            />
            <FaqItem
              q="How does ArgusSecure use AI?"
              a={(
                <>
                  <p>Two distinct uses, both grounded:</p>
                  <p><strong>Fix-It Agent.</strong> Claude Sonnet 4.5 receives each finding's evidence — code snippet, request/response pair, dependency line, CWE — and produces ONE structured fix: unified diff, version bump, or config change, with steps + verification.</p>
                  <p><strong>Reachability scoring.</strong> Hybrid heuristics rank findings by how exploitable they are in your specific application context — not generic CVSS.</p>
                  <p>What we do <em>not</em> do: hallucinate CVEs, fabricate analyst quotes, or pretend a generative model replaces a security review.</p>
                </>
              )}
            />
            <FaqItem
              q="Which integrations are available today?"
              a={(
                <>
                  <p>Today: GitHub repo connector (paste a URL), npm + PyPI manifest ingestion, JSON/PDF report export, signed API tokens for CI pipelines.</p>
                  <p>Next release: Slack notifications, Jira issue creation, GitHub App for PR checks. See the live roadmap on <Link to="/resources" className="text-gold underline">/resources</Link>.</p>
                </>
              )}
            />
            <FaqItem
              q="How does ArgusSecure handle third-party data?"
              a={(
                <p>All scan inputs are fed into an exposure data layer where they're correlated, normalized, and deduplicated against ArgusSecure's curated vulnerability intelligence. Findings carry full provenance — you can always see which scanner reported what, when, and why.</p>
              )}
            />
            <FaqItem
              q="Is ArgusSecure cloud-based?"
              a={(
                <p>Yes. The platform is a SaaS application built on FastAPI + MongoDB. On-prem and dedicated-tenant deployments are on the Enterprise roadmap — <DesignPartnerDialog source="platform-faq-onprem" trigger={<span className="text-gold underline cursor-pointer">talk to Security</span>} /> if that's a hard requirement.</p>
              )}
            />
            <FaqItem
              q="Who builds ArgusSecure?"
              a={(
                <p><strong>GSGD FIELD SOLUTIONS ENGINEERING</strong> — the only party responsible for the build. Every line of code, every page of marketing, every email signature traces back to a single engineering organization. No reseller logos, no co-author footnotes.</p>
              )}
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-panel/40">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h2 className="font-display text-5xl mb-4" data-testid="platform-final-cta">See ArgusSecure in action.</h2>
          <p className="text-ink/65 text-lg max-w-2xl mx-auto mb-10">
            Three demo projects are pre-scanned and waiting. Tour the findings, pop open the Fix-It Agent, then point ArgusSecure at something real.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/signup">
              <Button className="bg-gold text-paper hover:bg-gold2 h-14 px-8 font-semibold text-base" data-testid="platform-final-signup">
                Start My Free Scan <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <DesignPartnerDialog
              source="platform-final"
              trigger={
                <Button variant="outline" className="h-14 px-8 border-line bg-transparent text-ink hover:bg-raised text-base">
                  Request a demo
                </Button>
              }
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
